/**
 * Gerencia o estado dos botões de adicionar ao carrinho nos cards de produto
 * baseado nos limites de compra configurados (por produto ou por variante)
 */

class CardProductLimitManager {
  constructor() {
    this.cartData = null;
    this.debug = false;
    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[CardProductLimit]', ...args);
    }
  }

  isMobile() {
    return window.innerWidth < 990 || ('ontouchstart' in window && window.innerWidth < 990);
  }

  async init() {
    this.log('Inicializando CardProductLimitManager...');
    
    await this.loadCartData();
    this.log('Carrinho carregado:', this.cartData.items.length, 'itens');
    
    this.updateAllButtons();
    
    this.subscribeToCartUpdates();
    
    this.subscribeToVariantChanges();

    setTimeout(() => {
      this.log('Primeira verificação tardia - processando botões');
      this.updateAllButtons();
    }, 300);

    window.addEventListener('load', () => {
      this.log('Página carregada completamente - reprocessando botões');
      setTimeout(() => {
        this.updateAllButtons();
      }, 500);
    });
  }

  async loadCartData() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      this.cartData = await response.json();
    } catch (error) {
      console.error('Erro ao carregar dados do carrinho:', error);
      this.cartData = { items: [] };
    }
  }

  subscribeToCartUpdates() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        this.log('Carrinho atualizado via PUB_SUB_EVENTS');
        await this.loadCartData();
        this.updateAllButtons();
      });
    }

    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden) {
        this.log('Página ficou visível - recarregando dados do carrinho');
        await this.loadCartData();
        this.updateAllButtons();
      }
    });
  }

  subscribeToVariantChanges() {
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches('.card-product-add-btn') || node.querySelector('.card-product-add-btn')) {
              needsUpdate = true;
            }
          }
        });
      });
      if (needsUpdate) {
        this.updateAllButtons();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.attachVariantListeners();

    document.body.addEventListener('change', (event) => {
      const target = event.target;
      
      if (target.closest('variant-selects')) {
        const variantSelect = target.closest('variant-selects');
        setTimeout(() => {
          this.handleVariantChange(variantSelect);
        }, 200);
      }
      
      if (target.matches('input[name="id"].product-variant-id')) {
        const form = target.closest('form');
        if (!form) return;
        
        const button = form.querySelector('.card-product-add-btn');
        if (!button) return;
        
        button.setAttribute('data-variant-id', target.value);
        
        setTimeout(() => {
          this.updateButton(button, true);
        }, 200);
      }
    });
  }

  attachVariantListeners() {
    const variantSelects = document.querySelectorAll('variant-selects');
    
    variantSelects.forEach(variantSelect => {
      if (variantSelect.hasAttribute('data-limit-listener')) {
        return;
      }
      
      variantSelect.setAttribute('data-limit-listener', 'true');
      
      variantSelect.addEventListener('change', (event) => {
        setTimeout(() => {
          this.handleVariantChange(variantSelect);
        }, 200);
      });
    });
  }

  handleVariantChange(variantSelect) {
    const card = variantSelect.closest('.card-wrapper');
    if (!card) {
      this.log('Card não encontrado');
      return;
    }

    const button = card.querySelector('.card-product-add-btn');
    if (!button) {
      this.log('Botão não encontrado no card');
      return;
    }

    let variantId = null;

    if (variantSelect.currentVariant) {
      variantId = variantSelect.currentVariant.id;
      this.log('Variante obtida via currentVariant:', variantId);
    }

    if (!variantId) {
      const form = card.querySelector('form');
      if (form) {
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          variantId = variantInput.value;
          this.log('Variante obtida via input hidden:', variantId);
        }
      }
    }

    if (!variantId) {
      variantId = button.getAttribute('data-variant-id');
      this.log('Usando variante existente do botão:', variantId);
    }

    if (!variantId) {
      this.log('Variante não encontrada - abortando atualização');
      return;
    }

    button.setAttribute('data-variant-id', variantId);
    this.log('Atualizando botão com nova variante:', variantId);

    this.updateButton(button, true);

    setTimeout(() => {
      this.log('Verificação tardia do botão após mudança de variante');
      this.updateButton(button, true);
    }, 300);
  }

  updateAllButtons() {
    const buttons = document.querySelectorAll('.card-product-add-btn');
    this.log(`Atualizando ${buttons.length} botões de card`);
    buttons.forEach(button => this.updateButton(button));
  }

  updateButton(button, isVariantChange = false) {
    const soldOutDisabled = button.hasAttribute('data-sold-out-message') && 
                           button.querySelector('span')?.textContent.includes('Esgotado');
    if (soldOutDisabled) {
      return;
    }

    const productId = button.getAttribute('data-product-id');
    const variantId = button.getAttribute('data-variant-id');
    const limiteCompra = button.getAttribute('data-limite-compra') || 'Produto';
    const maxPerOrder = parseInt(button.getAttribute('data-quantidade-maxima'), 10) || 6;

    const isVariantLimit = limiteCompra.toLowerCase() === 'variante' || limiteCompra.toLowerCase() === 'variant';
    
    const isModalButton = button.closest('modal-opener') !== null;

    const wasDisabledByLimit = button.hasAttribute('data-disabled-by-limit');

    this.log('Atualizando botão:', {
      productId,
      variantId,
      limiteCompra,
      maxPerOrder,
      isVariantLimit,
      isModalButton,
      isVariantChange,
      wasDisabledByLimit
    });

    if (isVariantLimit) {
      if (isModalButton) {
        this.log('Modal button com limite por variante - sempre habilitado');
        this.enableButton(button);
      } else if (variantId) {
        const totalForVariant = this.getTotalForVariant(variantId);
        
        this.log(`Limite por variante: Variante ${variantId} tem ${totalForVariant}/${maxPerOrder} no carrinho`);
        
        if (totalForVariant >= maxPerOrder) {
          const variantMessage = `Limite de ${maxPerOrder} itens por variante atingido. Você pode selecionar outra variante deste produto.`;
          this.disableButton(button, variantMessage, false);
        } else {
          this.enableButton(button);
        }
      } else {
        this.log('Limite por variante mas sem variant ID - habilitando');
        this.enableButton(button);
      }
    } else if (productId) {
      const totalForProduct = this.getTotalForProduct(productId);
      
      this.log(`Limite por produto: Produto ${productId} tem ${totalForProduct}/${maxPerOrder} no carrinho`);
      
      if (totalForProduct >= maxPerOrder) {
        this.log(`Desabilitando por limite de produto. Mantendo texto original do botão.`);
        
        const productMessage = `Limite de ${maxPerOrder} itens por produto atingido.`;
        this.disableButton(button, productMessage, false);
      } else {
        this.enableButton(button);
      }
    }
  }

  getTotalForProduct(productId) {
    if (!this.cartData || !this.cartData.items) {
      return 0;
    }

    let total = 0;
    this.cartData.items.forEach(item => {
      if (String(item.product_id) === String(productId)) {
        total += item.quantity;
      }
    });
    return total;
  }

  getTotalForVariant(variantId) {
    if (!this.cartData || !this.cartData.items) {
      return 0;
    }

    let total = 0;
    this.cartData.items.forEach(item => {
      if (String(item.variant_id) === String(variantId)) {
        total += item.quantity;
      }
    });
    return total;
  }

  disableButton(button, message, changeText = false) {
    this.log('Desabilitando botão:', message, 'changeText:', changeText);
    
    button.disabled = true;
    button.classList.add('disabled');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('data-disabled-by-limit', 'true');
    button.setAttribute('data-limit-message', message);
    
    if (changeText) {
      const span = button.querySelector('span:not(.sold-out-message)');
      if (span) {
        const currentText = span.textContent.trim();
        
        if (!currentText.includes('Limite') && !currentText.includes('Esgotado')) {
          if (!span.hasAttribute('data-original-text')) {
            span.setAttribute('data-original-text', currentText);
          }
          
          this.log(`Mudando texto do botão de "${currentText}" para "Limite atingido"`);
          span.textContent = 'Limite atingido';
          span.setAttribute('data-limit-text-set', 'true');
        } else {
          this.log(`Texto do botão já é "${currentText}", não mudando`);
        }
      }
      
      setTimeout(() => {
        this.protectButtonText(button);
      }, 50);
    } else {
      this.log('changeText é false, mantendo texto atual do botão');
    }

    this.addTooltipListeners(button, message);
  }

  addTooltipListeners(button, message) {
    if (button._hoverListener) {
      button.removeEventListener('mouseenter', button._hoverListener);
      button.removeEventListener('mousemove', button._hoverListener);
      button.removeEventListener('mouseleave', button._leaveListener);
    }
    if (button._clickListener) {
      button.removeEventListener('click', button._clickListener, true);
    }
    if (button._touchListener) {
      button.removeEventListener('touchstart', button._touchListener, true);
    }

    const isMobile = this.isMobile();

    if (!isMobile) {
      button._hoverListener = (e) => {
        if (button.disabled && button.hasAttribute('data-disabled-by-limit')) {
          let buttonTooltip = button.parentElement.querySelector('.button-limit-tooltip');
          
          if (!buttonTooltip) {
            buttonTooltip = document.createElement('div');
            buttonTooltip.className = 'button-limit-tooltip';
            buttonTooltip.style.cssText = `
              position: absolute;
              bottom: 100%;
              left: 50%;
              transform: translateX(-50%);
              margin-bottom: 10px;
              background: #333;
              color: white;
              padding: 8px 12px;
              border-radius: 4px;
              font-size: 12px;
              line-height: 1.4;
              z-index: 100;
              pointer-events: none;
              opacity: 0;
              transition: opacity 0.2s;
              max-width: 280px;
              min-width: 200px;
              text-align: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              font-weight: 500;
              white-space: normal;
              word-wrap: break-word;
            `;
            
            const parent = button.parentElement;
            if (parent) {
              const parentPosition = window.getComputedStyle(parent).position;
              if (parentPosition === 'static') {
                parent.style.position = 'relative';
              }
              parent.appendChild(buttonTooltip);
            }
          }
          
          buttonTooltip.textContent = message;
          buttonTooltip.style.opacity = '1';
        }
      };

      button._leaveListener = () => {
        const buttonTooltip = button.parentElement?.querySelector('.button-limit-tooltip');
        if (buttonTooltip) {
          buttonTooltip.style.opacity = '0';
          setTimeout(() => buttonTooltip.remove(), 200);
        }
      };

      button.addEventListener('mouseenter', button._hoverListener);
      button.addEventListener('mouseleave', button._leaveListener);
    } else {
      button._touchListener = (e) => {
        if (button.disabled && button.hasAttribute('data-disabled-by-limit')) {
          e.preventDefault();
          e.stopPropagation();
          
          let buttonTooltip = button.parentElement.querySelector('.button-limit-tooltip');
          
          if (!buttonTooltip) {
            buttonTooltip = document.createElement('div');
            buttonTooltip.className = 'button-limit-tooltip';
            buttonTooltip.style.cssText = `
              position: absolute;
              bottom: 100%;
              left: 50%;
              transform: translateX(-50%);
              margin-bottom: 10px;
              background: #333;
              color: white;
              padding: 8px 12px;
              border-radius: 4px;
              font-size: 12px;
              line-height: 1.4;
              z-index: 100;
              pointer-events: none;
              opacity: 0;
              transition: opacity 0.2s;
              max-width: 280px;
              min-width: 200px;
              text-align: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              font-weight: 500;
              white-space: normal;
              word-wrap: break-word;
            `;
            
            const parent = button.parentElement;
            if (parent) {
              const parentPosition = window.getComputedStyle(parent).position;
              if (parentPosition === 'static') {
                parent.style.position = 'relative';
              }
              parent.appendChild(buttonTooltip);
            }
          }
          
          buttonTooltip.textContent = message;
          buttonTooltip.style.opacity = '1';
          
          document.querySelectorAll('.button-limit-tooltip').forEach(tip => {
            if (tip !== buttonTooltip) {
              tip.style.opacity = '0';
              setTimeout(() => tip.remove(), 200);
            }
          });
          
          if (button._tooltipTimeout) {
            clearTimeout(button._tooltipTimeout);
          }
          button._tooltipTimeout = setTimeout(() => {
            buttonTooltip.style.opacity = '0';
            setTimeout(() => buttonTooltip.remove(), 200);
          }, 3000);
        }
      };

      button._clickListener = (e) => {
        if (button.disabled && button.hasAttribute('data-disabled-by-limit')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      };

      button.addEventListener('touchstart', button._touchListener, true);
      button.addEventListener('click', button._clickListener, true);
    }
  }

  protectButtonText(button) {
    if (button.hasAttribute('data-text-observer')) {
      return;
    }

    const span = button.querySelector('span:not(.sold-out-message)');
    if (!span || !span.hasAttribute('data-limit-text-set')) return;

    let isUpdating = false;

    const observer = new MutationObserver((mutations) => {
      if (isUpdating) return;

      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          if (button.hasAttribute('data-disabled-by-limit') && 
              span.hasAttribute('data-limit-text-set') &&
              span.textContent !== 'Limite atingido') {
            
            isUpdating = true;
            this.log('Texto do botão foi alterado externamente, restaurando "Limite atingido"');
            span.textContent = 'Limite atingido';
            
            setTimeout(() => {
              isUpdating = false;
            }, 100);
          }
        }
      });
    });

    observer.observe(span, {
      characterData: true,
      childList: true,
      subtree: true
    });

    button.setAttribute('data-text-observer', 'true');
    button._textObserver = observer;
  }


  enableButton(button) {
    this.log('Habilitando botão');
    
    const span = button.querySelector('span:not(.sold-out-message)');
    if (span && (span.textContent.includes('Esgotado') || span.textContent === 'Sold out')) {
      this.log('Botão continua desabilitado - produto esgotado');
      return;
    }

    const form = button.closest('form');
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.disabled) {
        this.log('Botão continua desabilitado - variante sem estoque');
        return;
      }
    }

    if (button._textObserver) {
      button._textObserver.disconnect();
      delete button._textObserver;
      button.removeAttribute('data-text-observer');
    }

    this.removeTooltipListeners(button);

    button.disabled = false;
    button.classList.remove('disabled');
    button.removeAttribute('aria-disabled');
    button.removeAttribute('data-disabled-by-limit');
    button.removeAttribute('data-limit-message');
    
    if (span && span.hasAttribute('data-original-text')) {
      const originalText = span.getAttribute('data-original-text');
      this.log(`Restaurando texto do botão de "${span.textContent}" para "${originalText}"`);
      span.textContent = originalText;
      span.removeAttribute('data-original-text');
      span.removeAttribute('data-limit-text-set');
    } else {
      this.log(`Botão habilitado, mantendo texto atual: "${span?.textContent}"`);
    }
  }

  removeTooltipListeners(button) {
    if (button._hoverListener) {
      button.removeEventListener('mouseenter', button._hoverListener);
      delete button._hoverListener;
    }
    if (button._leaveListener) {
      button.removeEventListener('mouseleave', button._leaveListener);
      delete button._leaveListener;
    }
    if (button._clickListener) {
      button.removeEventListener('click', button._clickListener, true);
      delete button._clickListener;
    }
    if (button._touchListener) {
      button.removeEventListener('touchstart', button._touchListener, true);
      delete button._touchListener;
    }
    if (button._tooltipTimeout) {
      clearTimeout(button._tooltipTimeout);
      delete button._tooltipTimeout;
    }
    
    const buttonTooltip = button.parentElement?.querySelector('.button-limit-tooltip');
    if (buttonTooltip) {
      buttonTooltip.remove();
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardProductLimitManager();
  });
} else {
  new CardProductLimitManager();
}
